import { Info } from 'lucide-react'

import carbonTree from '@/assets/images/carbon-simulation/carbon-tree.png'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTrigger,
} from '@/components/dialog'

interface RiskOfDeforestationWithoutConservationProps {
  projectAreaWithoutConservation: string
  totalCo2ReleasedByDeforestation: string
  deforestationCarbonReleaseFactor: string
  expectedDeforestationRate: string
}

export function RiskOfDeforestationWithoutConservation({
  deforestationCarbonReleaseFactor,
  expectedDeforestationRate,
  projectAreaWithoutConservation,
  totalCo2ReleasedByDeforestation,
}: RiskOfDeforestationWithoutConservationProps) {
  return (
    <div className="order-4 col-span-6 flex w-full flex-col rounded-2xl shadow-2xl lg:order-3 lg:col-span-4 lg:h-144">
      {/* SESSÃO 1 */}
      <div className="flex h-2/3 flex-col gap-8 space-y-10 rounded-t-2xl bg-bios-yellow-100 px-8 py-12 lg:flex-row lg:space-y-0 lg:py-0 lg:pt-10">
        <div className="flex w-full flex-col items-start justify-between">
          <h1 className="size-full text-4xl font-bold text-bios-green-1050">
            Risco de desmatamento sem conservação
          </h1>
          <div className="hidden w-4/5 items-end md:flex">
            <img
              src={carbonTree}
              alt=""
              className="size-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="flex size-full flex-col items-center lg:flex-row lg:items-start">
          <div className="grid gap-8 px-4 lg:grid-cols-1 lg:px-0 lg:pb-12 lg:pr-12">
            <div className="max-w-64 space-y-2">
              <p className="text-lg font-bold text-bios-lime-800">
                Área em risco
              </p>
              <p className="flex items-center gap-1 border-l-4 border-bios-secondary pl-2 text-bios-green-2000">
                <strong className="text-3xl">
                  {projectAreaWithoutConservation}
                  ha
                </strong>
              </p>
            </div>

            <div className="max-w-64 space-y-2">
              <p className="text-lg font-bold text-bios-lime-800">
                Fator de liberação de carbono
              </p>
              <p className="text-bold flex items-center gap-1 border-l-4 border-bios-secondary pl-2 text-bios-green-2000">
                <strong className="text-3xl">
                  {deforestationCarbonReleaseFactor}%
                </strong>
              </p>
            </div>

            <div className="max-w-64 space-y-2">
              <p className="text-lg font-bold text-bios-lime-800">
                CO₂ liberado se desmatado
              </p>
              <p className="text-bold flex items-center gap-1 border-l-4 border-bios-secondary pl-2 text-bios-green-2000">
                <strong className="text-3xl">
                  {totalCo2ReleasedByDeforestation}
                </strong>{' '}
                tCO₂e/ha
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* SESSÃO 2 */}

      <div className="relative flex h-1/3 flex-col items-center justify-center gap-2 rounded-b-2xl bg-linear-to-r from-bios-darkGreen-50 to-bios-green-400 px-8 py-8 text-center">
        <p className="text-3xl font-bold text-white">
          Taxa anual de desmatamento esperada (Baseline)
        </p>

        <p className="text-xl text-white">
          <strong className="text-5xl text-bios-green-50">
            {expectedDeforestationRate}%
          </strong>{' '}
          ao ano
        </p>

        <Dialog>
          <DialogTrigger asChild>
            <Info className="absolute right-5 top-5 size-7 cursor-pointer rounded-full fill-white text-bios-green-400" />
          </DialogTrigger>

          <DialogContent className="lg-py-0 h-auto py-10">
            <DialogDescription className="space-y-4 text-monochromatic-500">
              <p className="lg:paragraph">
                O índice aqui apresentado utiliza uma área de referência de 100
                x 100 km (10.000 km2) tendo como ponto central o centro
                geométrico da área de referência. Adota um período de análise
                dos 5 anos anteriores (janela temporal de referência), exibida
                de forma anualizada.
              </p>
              <p className="lg:paragraph">
                As fontes de cobertura florestal são: Hansen, M. C., P. V.
                Potapov, R. Moore, M. Hancher, S. A. Turubanova, A. Tyukavina,
                D. Thau, S. V. Stehman, S. J. Goetz, T. R. Loveland, A.
                Kommareddy, A. Egorov, L. Chini, C. O. Justice, and J. R. G)
                Townshend. 2013. &quot;Mapas globais de alta resolução das
                mudanças na cobertura florestal do século 21&quot;. Science 342
                (15 de novembro): 850-53.
              </p>
            </DialogDescription>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
